/**
 * Which route this server's own settings ask for (plan
 * `docs/plans/LOCAL-HTTP-AND-CLOUD-ADOPTION.md` §2/C4, card `t_16b7704a`).
 *
 * The per-server, per-device settings live in the buffered profile
 * (`services/serverProfiles.ts`, card `t_f4f925f9`): `cloudOnly` (D2 — the
 * returning switch, *"cloud is the default with local override"*) and
 * `manualAddress` + its `manual` flag. This module is the ONE reading of them,
 * and it answers the connect path a value instead of a condition:
 *
 *     1. `cloudOnly` → **the tunnel, and the LAN is never dialled** — not as a
 *        first try, not as a probe (`'cloud-only'`);
 *     2. `manual` with a readable address → **that address first**, then the
 *        tunnel — the owner's *"Kolomban on VPN"* case, where discovery finds
 *        nothing at all (`'manual'`);
 *     3. neither → today's automatic local-first (`'auto'`).
 *
 * Order is the card's and the `manual` address is deliberately **not** "the
 * address to prefer among discovered ones": it is the only address dialled for a
 * hub nothing can see, so a manual answer outranks the switch's *absence* the
 * same way it outranks a discovered address.
 *
 * A `cloudOnly` server that also carries a manual address keeps it (the record
 * is the device's own, and unticking the switch must return the hub to the
 * address it had) — hence `manualAddress` on the `cloud-only` value: it is what
 * the settings sheet shows beside the switch, not a route.
 *
 * Nothing here dials, stores or asks: it reads the buffer and classifies
 * (`utils/hubAddress.ts` owns what a typed address means). The connect path
 * (`services/hubConnect.ts` → `connectResolvingRoute`) spends it.
 */
import type { ServerProfile, ServerProfileStorage } from './serverProfiles';
import { hydrateServerProfiles, profileFor, serverProfiles } from './serverProfiles';
import { parseHubAddressInput, type HubAddressInput } from '../utils/hubAddress';

/** What this server's settings ask the connect path for. */
export type ServerConnectPreference =
  /** Pin the server to the tunnel: the LAN address is never dialled. */
  | { kind: 'cloud-only'; manualAddress: HubAddressInput | null }
  /** Dial exactly this address first, then the tunnel. */
  | { kind: 'manual'; address: HubAddressInput; raw: string }
  /** Nothing stored: today's automatic local-first route. */
  | { kind: 'auto' };

/** The profile fields this decision reads — nothing else of the record matters here. */
export type RoutePreferenceFacts = Pick<ServerProfile, 'cloudOnly' | 'manual' | 'manualAddress'>;

/**
 * The classification itself, from the record's own fields.
 *
 * A `manual` flag without a readable address is **not** a manual route: the
 * record may have been written by a build that spelled addresses differently, and
 * dialling nothing is not a route. It falls through to `auto`, which is what an
 * unreadable setting should do — the card's promise is about what the person
 * asked for, and nothing readable was asked for.
 */
export function connectPreferenceFor(profile: RoutePreferenceFacts | null | undefined): ServerConnectPreference {
  if (!profile) return { kind: 'auto' };
  const address = parseHubAddressInput(profile.manualAddress);
  if (profile.cloudOnly) return { kind: 'cloud-only', manualAddress: address };
  if (profile.manual && address) return { kind: 'manual', address, raw: (profile.manualAddress ?? '').trim() };
  return { kind: 'auto' };
}

/**
 * The same answer for one server of the account, read out of the device's own
 * buffer. The ids are the two spellings the record can be keyed by (the cloud
 * row's id and the hub's own) — `profileFor` matches either, so a caller that
 * knows only one still finds its record.
 *
 * Hydrates first: the buffer is read from storage once per session, and a connect
 * can be the first thing that happens after a launch (`/tab-connect` in a shell
 * tab), where nothing has painted the launcher yet.
 */
export function serverConnectPreference(
  ref: {
    cloudId?: string | null;
    hubId?: string | null;
  },
  options: { storage?: ServerProfileStorage | null } = {},
): ServerConnectPreference {
  hydrateServerProfiles(options.storage === undefined ? {} : { storage: options.storage });
  return connectPreferenceFor(
    profileFor({ cloudId: ref.cloudId ?? null, hubId: ref.hubId ?? null }, serverProfiles.value),
  );
}
